import _ from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { Fragment, useState, useCallback } from 'react';

import List from '@mui/material/List';
import Stack from '@mui/system/Stack';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import Pagination from '@mui/material/Pagination';
import DialogTitle from '@mui/material/DialogTitle';
import ListItemText from '@mui/material/ListItemText';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import { Box, Button, Tooltip, IconButton } from '@mui/material';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { rootApi } from 'src/services/rootApi';
import { useAppDispatch } from 'src/store/store';
import { PageOptions } from 'src/services/api.types';
import { JobInvite } from 'src/services/jobs/jobs.types';
import { isOrganisationTenant } from 'src/services/auth/auth.utils';
import { generateJobInviteResendError } from 'src/services/jobs/jobs.utils';
import { hidePendingJobInvitesModal } from 'src/store/slices/jobs/jobsSlice';
import { ConnectionTarget } from 'src/services/connections/connections.types';
import {
  useCancelJobInviteMutation,
  useResendJobInviteMutation,
  useGetOutgoingJobInvitesQuery,
} from 'src/services/jobs/jobs.service';

import Iconify from 'src/components/iconify';
import EmptyContent from 'src/components/empty-content';
import ListSkeleton from 'src/components/list/list-skeleton';

import { BusinessErrorCodes } from 'src/types/business-errors';

type Props = {
  open: boolean;
  scopedJobId?: string;
};

const DEFAULT_PAGE: PageOptions = {
  page: 1,
  per_page: 5,
};

export default function PendingJobInviteModal({ open, scopedJobId }: Props) {
  const { t } = useTranslate();

  const [target] = useState<ConnectionTarget>(ConnectionTarget.Incoming);

  const dispatch = useAppDispatch();

  const tenant = useOrgTenant();

  const onClose = useCallback(() => {
    dispatch(hidePendingJobInvitesModal());
  }, [dispatch]);

  const [pageOptions, setPageOptions] = useState<PageOptions>(DEFAULT_PAGE);

  const { currentData: inviteData, isFetching: invitesLoading } = useGetOutgoingJobInvitesQuery(
    {
      job_id: scopedJobId || undefined,
      ...pageOptions,
    },
    {
      skip: !isOrganisationTenant(tenant)
    }
  );

  const [cancelInvite, { isLoading: isCancelling }] = useCancelJobInviteMutation();

  const [resendInvite, { isLoading: isResending }] = useResendJobInviteMutation();

  const changePage = useCallback(
    (page: number) => {
      setPageOptions((prev) => ({
        ...prev,
        page,
      }));
    },
    [setPageOptions]
  );

  const handleCancelInvite = useCallback(
    async (inviteId: string) => {
      try {
        await cancelInvite(inviteId).unwrap();

        enqueueSnackbar(t('jobs.pending_invite_modal.api.cancel.success'), { variant: 'success' });
      } catch (error) {
        enqueueSnackbar(t('jobs.pending_invite_modal.api.cancel.default_error'), {
          variant: 'error',
        });
      }
    },
    [cancelInvite, t]
  );

  const handleResendInvite = useCallback(
    async (inviteId: string) => {
      try {
        await resendInvite(inviteId).unwrap();

        enqueueSnackbar(t('jobs.pending_invite_modal.api.resend.success'), { variant: 'success' });
      } catch (error) {

        if (error?.data.error_code === BusinessErrorCodes.JobInviteExpired) {
          dispatch(rootApi.util.invalidateTags([{ type: 'JobInvites' }]));
        }

        enqueueSnackbar(generateJobInviteResendError(error), {
          variant: 'error',
        });
      }
    },
    [resendInvite, t, dispatch]
  );

  const renderRequest = (request: JobInvite, index: number) => {
    const jobTitle = request.job?.title;
    const candidateName = `${request.first_name} ${request.last_name}`;
    const candidateEmail = request.email;

    return (
      <Fragment key={`request-${index}`}>
        <ListItem sx={{ mb: 1 }}>
          <ListItemAvatar>
            <Avatar>{candidateName?.charAt(0)}</Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={jobTitle}
            primaryTypographyProps={{ sx: { textTransform: 'capitalize', fontWeight: 500 } }}
            secondary={_.capitalize(`${candidateName} - ${candidateEmail}`)}
            secondaryTypographyProps={{
              paddingRight: 14,
              sx: { fontWeight: 400, fontSize: 13 },
            }}
          />
          <IconButton onClick={() => handleResendInvite(request.id)} disabled={isResending}>
            <Tooltip title={t('jobs.pending_invite_modal.tooltips.resend')}>
              <Iconify icon="mdi:email-resend-outline" />
            </Tooltip>
          </IconButton>
          <IconButton onClick={() => handleCancelInvite(request.id)} disabled={isCancelling}>
            <Tooltip title={t('jobs.pending_invite_modal.tooltips.cancel')}>
              <Iconify icon="gg:trash" color="red" />
            </Tooltip>
          </IconButton>
        </ListItem>

        <Divider variant="middle" component="li" />
      </Fragment>
    );
  };

  const renderInvites = (
    <Stack direction="column">
      <Alert variant="outlined" severity="info" sx={{ mb: 3 }}>
        {t(`jobs.pending_invite_modal.alert`)}
      </Alert>

      {invitesLoading && (
        <ListSkeleton />
      )}

      {
        !invitesLoading && (
          <>
            {inviteData?.count ? (
              <List dense>
                {inviteData?.results?.map((requestObj, requestIndex) =>
                  renderRequest(requestObj, requestIndex)
                )}
              </List>
            ) : (
              <EmptyContent
                filled
                title="No Data"
                sx={{
                  py: 10,
                }}
              />
            )}

            <Stack flexDirection="row" justifyContent="center" sx={{ pt: 2 }}>
              <Pagination
                page={pageOptions.page ?? 1}
                count={Math.ceil((inviteData?.count ?? 1) / pageOptions.per_page)}
                onChange={(_e, value) => changePage(value)}
              />
            </Stack>
          </>
        )
      }

    </Stack>
  );

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogTitle sx={{ textTransform: 'capitalize' }}>
        {t('jobs.pending_invite_modal.title', {
          target: target === 'incoming' ? t('common.incoming') : t('common.outgoing'),
        })}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ width: '100%', typography: 'body1' }}>{renderInvites}</Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
